import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import { 
  Plus, Search, Star, MapPin, Phone, Mail, Globe, User,
  Edit2, X, ChevronDown, ChevronRight, AlertTriangle,
  Sparkles, Building2, Package, Plane, Eye, EyeOff, ExternalLink, Trash2,
  Settings, AlertCircle, Tag
} from 'lucide-react';

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

function CategoryBadge({ category, categories, isSecondary = false }) {
  const info = categories.find(c => c.name === category);
  const color = info?.color || 'bg-gray-100 text-gray-700';
  const label = info?.label || category;
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${color} ${isSecondary ? 'opacity-70' : ''}`}>
      {label}
    </span>
  );
}

function MarketBadge({ market, isHome }) {
  return (
    <span className={`px-2 py-0.5 rounded text-xs ${isHome ? 'bg-hub-100 text-hub-700 font-medium' : 'bg-gray-100 text-gray-600'}`}>
      {market}
    </span>
  );
}

// ============================================================================
// CONFIRM DELETE MODAL
// ============================================================================

function ConfirmDeleteModal({ vendor, onConfirm, onClose, deleting }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold text-red-600">Delete Vendor</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete <strong>{vendor.name}</strong>?
          </p>
          <p className="text-sm text-gray-500">
            This action cannot be undone. All services associated with this vendor will also be deleted.
          </p>
          <div className="flex gap-3">
            <button 
              onClick={onConfirm} 
              className="btn-primary flex-1 bg-red-600 hover:bg-red-700" 
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete Vendor'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ADD MARKET MODAL
// ============================================================================

function AddMarketModal({ onSave, onClose }) {
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setSaving(true);
    try {
      await api.post('/markets', { name: name.trim(), region: region.trim() || null });
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add Market</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Market Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. Austin" required autoFocus />
          </div>
          <div>
            <label className="label">Region</label>
            <input value={region} onChange={e => setRegion(e.target.value)} className="input" placeholder="e.g. Texas" />
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Adding...' : 'Add Market'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// ADD CATEGORY MODAL
// ============================================================================

function AddCategoryModal({ onSave, onClose }) {
  const [name, setName] = useState('');
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);

  const colors = [
    'bg-purple-100 text-purple-700',
    'bg-pink-100 text-pink-700',
    'bg-blue-100 text-blue-700',
    'bg-amber-100 text-amber-700',
    'bg-green-100 text-green-700',
    'bg-red-100 text-red-700',
    'bg-cyan-100 text-cyan-700',
    'bg-orange-100 text-orange-700',
    'bg-teal-100 text-teal-700',
    'bg-indigo-100 text-indigo-700',
  ];
  const [color, setColor] = useState(colors[0]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !label.trim()) return;
    setSaving(true);
    try {
      await api.post('/event-categories', { name: name.trim().toUpperCase().replace(/\s+/g, '_'), label: label.trim(), color });
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Add Category</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="label">Display Label *</label>
            <input value={label} onChange={e => setLabel(e.target.value)} className="input" placeholder="e.g. Photo / Video" required autoFocus />
          </div>
          <div>
            <label className="label">Code Name *</label>
            <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="e.g. PHOTO_VIDEO" required />
            <p className="text-xs text-gray-500 mt-1">Uppercase, no spaces</p>
          </div>
          <div>
            <label className="label">Color</label>
            <div className="flex flex-wrap gap-2">
              {colors.map(c => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded ${c} ${color === c ? 'ring-2 ring-hub-600 ring-offset-2' : ''}`}
                />
              ))}
            </div>
          </div>
          <div className="flex gap-3">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>{saving ? 'Adding...' : 'Add Category'}</button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// MANAGE MARKETS MODAL
// ============================================================================

function ManageMarketsModal({ markets, vendors, onUpdate, onClose }) {
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editRegion, setEditRegion] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleteTyped, setDeleteTyped] = useState('');
  const [saving, setSaving] = useState(false);

  // Count vendors using each market
  const getVendorCount = (marketName) => {
    return vendors.filter(v => 
      v.homeMarket === marketName || 
      v.additionalMarkets?.includes(marketName)
    ).length;
  };

  const startEdit = (market) => {
    setEditingId(market.id);
    setEditName(market.name);
    setEditRegion(market.region || '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditRegion('');
  };

  const saveEdit = async () => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.put(`/markets/${editingId}`, { 
        name: editName.trim(), 
        region: editRegion.trim() || null 
      });
      onUpdate();
      cancelEdit();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm || deleteTyped !== deleteConfirm.name) return;
    setSaving(true);
    try {
      await api.delete(`/markets/${deleteConfirm.id}`);
      setDeleteConfirm(null);
      setDeleteTyped('');
      onUpdate();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">Manage Markets</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {markets.length === 0 ? (
            <p className="text-gray-500 text-center py-4">No markets defined yet.</p>
          ) : (
            <div className="space-y-2">
              {markets.map(market => {
                const vendorCount = getVendorCount(market.name);
                const isEditing = editingId === market.id;
                
                return (
                  <div key={market.id} className="border rounded-lg p-3">
                    {isEditing ? (
                      <div className="space-y-3">
                        <div className="grid grid-cols-2 gap-2">
                          <input 
                            value={editName} 
                            onChange={e => setEditName(e.target.value)} 
                            className="input text-sm" 
                            placeholder="Market name"
                            autoFocus
                          />
                          <input 
                            value={editRegion} 
                            onChange={e => setEditRegion(e.target.value)} 
                            className="input text-sm" 
                            placeholder="Region (optional)"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveEdit} disabled={saving} className="btn-primary text-xs px-3 py-1">
                            {saving ? 'Saving...' : 'Save'}
                          </button>
                          <button onClick={cancelEdit} className="btn-secondary text-xs px-3 py-1">Cancel</button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{market.name}</div>
                          <div className="text-xs text-gray-500">
                            {market.region && <span>{market.region} • </span>}
                            {vendorCount} vendor{vendorCount !== 1 ? 's' : ''}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button 
                            onClick={() => startEdit(market)} 
                            className="p-2 hover:bg-gray-100 rounded text-gray-500"
                            title="Edit"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button 
                            onClick={() => setDeleteConfirm(market)} 
                            className="p-2 hover:bg-red-50 rounded text-gray-400 hover:text-red-600"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="p-4 border-t">
          <button onClick={onClose} className="btn-secondary w-full">Done</button>
        </div>

        {/* Delete Confirmation Overlay */}
        {deleteConfirm && (
          <div className="absolute inset-0 bg-white/95 rounded-xl flex items-center justify-center p-6">
            <div className="max-w-sm space-y-4">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-8 h-8" />
                <h3 className="font-semibold text-lg">Delete Market</h3>
              </div>
              
              <p className="text-gray-700">
                Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
              </p>
              
              {getVendorCount(deleteConfirm.name) > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800">
                    <strong>Warning:</strong> {getVendorCount(deleteConfirm.name)} vendor(s) reference this market. 
                    They will need to be updated.
                  </p>
                </div>
              )}
              
              <div>
                <label className="label text-sm">Type "{deleteConfirm.name}" to confirm:</label>
                <input 
                  value={deleteTyped}
                  onChange={e => setDeleteTyped(e.target.value)}
                  className="input"
                  placeholder={deleteConfirm.name}
                  autoFocus
                />
              </div>
              
              <div className="flex gap-3">
                <button 
                  onClick={handleDelete}
                  disabled={deleteTyped !== deleteConfirm.name || saving}
                  className="btn-primary flex-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-300"
                >
                  {saving ? 'Deleting...' : 'Delete Market'}
                </button>
                <button 
                  onClick={() => { setDeleteConfirm(null); setDeleteTyped(''); }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// VENDOR MODAL (Add/Edit) - Now with Additional Categories
// ============================================================================

function VendorModal({ vendor, markets, categories, onSave, onClose }) {
  const [form, setForm] = useState({
    name: vendor?.name || '',
    category: vendor?.category || '',
    additionalCategories: vendor?.additionalCategories || [],  // NEW: Multi-category support
    homeMarket: vendor?.homeMarket || '',
    additionalMarkets: vendor?.additionalMarkets || [],
    willTravel: vendor?.willTravel || false,
    contactName: vendor?.contactName || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    website: vendor?.website || '',
    address: vendor?.address || '',
    notes: vendor?.notes || '',
    isPreferred: vendor?.isPreferred || false,
    status: vendor?.status || 'ACTIVE'
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const toggleMarket = (market) => {
    setForm(prev => ({
      ...prev,
      additionalMarkets: prev.additionalMarkets.includes(market)
        ? prev.additionalMarkets.filter(m => m !== market)
        : [...prev.additionalMarkets, market]
    }));
  };

  // NEW: Toggle additional category
  const toggleCategory = (categoryName) => {
    setForm(prev => ({
      ...prev,
      additionalCategories: prev.additionalCategories.includes(categoryName)
        ? prev.additionalCategories.filter(c => c !== categoryName)
        : [...prev.additionalCategories, categoryName]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.category) {
      alert('Name and category are required');
      return;
    }
    setSaving(true);
    try {
      if (vendor?.id) {
        await api.put(`/vendors/${vendor.id}`, form);
      } else {
        await api.post('/vendors', form);
      }
      onSave();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50 overflow-y-auto">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl my-8 animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <h2 className="text-lg font-semibold">{vendor?.id ? 'Edit Vendor' : 'New Vendor'}</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* QuickBooks Warning */}
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700">Vendor name must match <strong>exactly</strong> as it appears in QuickBooks.</p>
          </div>

          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="label">Vendor Name *</label>
              <input value={form.name} onChange={e => handleChange('name', e.target.value)} className="input" required placeholder="Exact name as in QuickBooks" />
            </div>
            <div>
              <label className="label">Primary Category *</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)} className="input" required>
                <option value="">Select category...</option>
                {categories.map(c => <option key={c.id} value={c.name}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex items-end gap-4 pb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.isPreferred} onChange={e => handleChange('isPreferred', e.target.checked)} className="w-4 h-4 rounded text-amber-500" />
                <Star className={`w-4 h-4 ${form.isPreferred ? 'fill-amber-400 text-amber-500' : 'text-gray-400'}`} />
                <span className="text-sm">Preferred Vendor</span>
              </label>
            </div>
          </div>

          {/* NEW: Additional Categories */}
          <div className="space-y-2">
            <label className="label flex items-center gap-2">
              <Tag className="w-4 h-4" /> Also Provides (Additional Categories)
            </label>
            <p className="text-xs text-gray-500">Select other services this vendor can provide</p>
            <div className="flex flex-wrap gap-2">
              {categories.filter(c => c.name !== form.category).map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => toggleCategory(c.name)}
                  className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                    form.additionalCategories.includes(c.name) 
                      ? c.color || 'bg-hub-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {/* Markets */}
          <div className="space-y-3">
            <label className="label">Markets</label>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Home Market</label>
                <select value={form.homeMarket} onChange={e => handleChange('homeMarket', e.target.value)} className="input">
                  <option value="">None</option>
                  {markets.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
              <div className="flex items-end pb-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.willTravel} onChange={e => handleChange('willTravel', e.target.checked)} className="w-4 h-4 rounded text-hub-600" />
                  <Plane className="w-4 h-4 text-gray-500" />
                  <span className="text-sm">Will Travel (All Markets)</span>
                </label>
              </div>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Also Services</label>
              <div className="flex flex-wrap gap-2">
                {markets.filter(m => m.name !== form.homeMarket).map(m => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMarket(m.name)}
                    className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                      form.additionalMarkets.includes(m.name) 
                        ? 'bg-hub-600 text-white' 
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {m.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Contact Name</label>
              <input value={form.contactName} onChange={e => handleChange('contactName', e.target.value)} className="input" />
            </div>
            <div>
              <label className="label">Phone</label>
              <input value={form.phone} onChange={e => handleChange('phone', e.target.value)} className="input" type="tel" />
            </div>
            <div>
              <label className="label">Email</label>
              <input value={form.email} onChange={e => handleChange('email', e.target.value)} className="input" type="email" />
            </div>
            <div>
              <label className="label">Website</label>
              <input value={form.website} onChange={e => handleChange('website', e.target.value)} className="input" placeholder="https://" />
            </div>
            <div className="col-span-2">
              <label className="label">Address</label>
              <input value={form.address} onChange={e => handleChange('address', e.target.value)} className="input" placeholder="123 Main St, Las Vegas, NV 89101" />
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="label">Notes</label>
            <textarea value={form.notes} onChange={e => handleChange('notes', e.target.value)} className="input" rows={3} placeholder="Internal notes about this vendor..." />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2 border-t">
            <button type="submit" className="btn-primary flex-1" disabled={saving}>
              {saving ? 'Saving...' : vendor?.id ? 'Save Changes' : 'Add Vendor'}
            </button>
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================================================
// SERVICES MODAL
// ============================================================================

function ServicesModal({ vendor, onClose, onUpdate }) {
  const [services, setServices] = useState(vendor.services || []);
  const [saving, setSaving] = useState(false);
  const [newService, setNewService] = useState({ description: '', defaultCost: '', defaultPrice: '' });

  const addService = () => {
    if (!newService.description) return;
    setServices(prev => [...prev, {
      id: `temp-${Date.now()}`,
      description: newService.description,
      defaultCost: parseFloat(newService.defaultCost) || 0,
      defaultPrice: parseFloat(newService.defaultPrice) || 0
    }]);
    setNewService({ description: '', defaultCost: '', defaultPrice: '' });
  };

  const removeService = (id) => {
    setServices(prev => prev.filter(s => s.id !== id));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/vendors/${vendor.id}/services`, { services });
      onUpdate();
      onClose();
    } catch (err) {
      alert(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-xl animate-slide-up">
        <div className="p-4 border-b flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Manage Services</h2>
            <p className="text-sm text-gray-500">{vendor.name}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
          <p className="text-xs text-gray-500">
            Define the services this vendor provides with default pricing. These can be used when building quotes.
          </p>

          {services.length > 0 && (
            <div className="space-y-2">
              {services.map(s => (
                <div key={s.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                  <div className="flex-1 truncate text-sm">{s.description}</div>
                  <div className="text-sm text-gray-500 whitespace-nowrap">
                    ${s.defaultCost} → ${s.defaultPrice}
                  </div>
                  <button onClick={() => removeService(s.id)} className="text-red-500 hover:text-red-700 p-1">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <label className="label">Add Service</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={newService.description}
                onChange={e => setNewService(p => ({ ...p, description: e.target.value }))}
                className="input flex-1 text-sm"
                placeholder="Service description..."
              />
              <div className="relative w-24">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  value={newService.defaultCost}
                  onChange={e => setNewService(p => ({ ...p, defaultCost: e.target.value }))}
                  className="input text-sm pl-6"
                  placeholder="Cost"
                />
              </div>
              <div className="relative w-24">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                <input
                  type="number"
                  value={newService.defaultPrice}
                  onChange={e => setNewService(p => ({ ...p, defaultPrice: e.target.value }))}
                  className="input text-sm pl-6"
                  placeholder="Price"
                />
              </div>
              <button onClick={addService} className="btn-secondary px-3"><Plus className="w-4 h-4" /></button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t flex gap-3">
          <button onClick={handleSave} disabled={saving} className="btn-primary flex-1">
            {saving ? 'Saving...' : 'Save Services'}
          </button>
          <button onClick={onClose} className="btn-secondary flex-1">Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// VENDOR CARD - Now shows additional categories
// ============================================================================

function VendorCard({ vendor, categories, onEdit, onServices, onToggleStatus, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const isInactive = vendor.status === 'INACTIVE';

  return (
    <div className={`border rounded-lg transition-all ${isInactive ? 'opacity-60 bg-gray-50' : 'bg-white hover:shadow-md'}`}>
      <div className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              {vendor.isPreferred && <Star className="w-4 h-4 fill-amber-400 text-amber-500 shrink-0" />}
              <h3 className="font-semibold truncate">{vendor.name}</h3>
              {isInactive && <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">Inactive</span>}
            </div>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <CategoryBadge category={vendor.category} categories={categories} />
              {/* Show count of additional categories if any */}
              {vendor.additionalCategories?.length > 0 && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs">
                  +{vendor.additionalCategories.length} more
                </span>
              )}
              {vendor.homeMarket && <MarketBadge market={vendor.homeMarket} isHome />}
              {vendor.willTravel && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs flex items-center gap-1">
                  <Plane className="w-3 h-3" /> Will Travel
                </span>
              )}
            </div>
          </div>
          <button onClick={() => setExpanded(!expanded)} className="p-2 hover:bg-gray-100 rounded-lg">
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>

        <div className="flex items-center gap-4 mt-3 text-sm text-gray-600 flex-wrap">
          {vendor.contactName && (
            <span className="flex items-center gap-1"><User className="w-3 h-3" /> {vendor.contactName}</span>
          )}
          {vendor.phone && (
            <a href={`tel:${vendor.phone}`} className="flex items-center gap-1 text-hub-600 hover:underline">
              <Phone className="w-3 h-3" /> {vendor.phone}
            </a>
          )}
          {vendor.services?.length > 0 && (
            <span className="flex items-center gap-1"><Package className="w-3 h-3" /> {vendor.services.length} services</span>
          )}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t pt-3">
          {vendor.address && (
            <div className="flex items-start gap-2 text-sm text-gray-600">
              <MapPin className="w-4 h-4 shrink-0 mt-0.5" />
              <span>{vendor.address}</span>
            </div>
          )}
          
          {/* NEW: Show additional categories */}
          {vendor.additionalCategories?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Also Provides:</p>
              <div className="flex flex-wrap gap-1">
                {vendor.additionalCategories.map(cat => (
                  <CategoryBadge key={cat} category={cat} categories={categories} isSecondary />
                ))}
              </div>
            </div>
          )}
          
          {vendor.additionalMarkets?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Also Services:</p>
              <div className="flex flex-wrap gap-1">
                {vendor.additionalMarkets.map(m => <MarketBadge key={m} market={m} />)}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            {vendor.email && (
              <a href={`mailto:${vendor.email}`} className="flex items-center gap-2 text-hub-600 hover:underline">
                <Mail className="w-4 h-4" /> {vendor.email}
              </a>
            )}
            {vendor.website && (
              <a href={vendor.website.startsWith('http') ? vendor.website : `https://${vendor.website}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-hub-600 hover:underline">
                <Globe className="w-4 h-4" /> Website <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>

          {vendor.services?.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-1">Services:</p>
              <div className="space-y-1">
                {vendor.services.slice(0, 3).map(s => (
                  <div key={s.id} className="flex justify-between text-sm bg-gray-50 px-2 py-1 rounded">
                    <span className="truncate">{s.description}</span>
                    <span className="text-green-600 font-medium">${s.defaultPrice}</span>
                  </div>
                ))}
                {vendor.services.length > 3 && (
                  <p className="text-xs text-gray-400">+{vendor.services.length - 3} more</p>
                )}
              </div>
            </div>
          )}

          {vendor.notes && (
            <div className="text-sm text-gray-600 bg-gray-50 p-2 rounded">{vendor.notes}</div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={() => onEdit(vendor)} className="btn-secondary text-sm flex-1">
              <Edit2 className="w-3 h-3" /> Edit
            </button>
            <button onClick={() => onServices(vendor)} className="btn-secondary text-sm flex-1">
              <Package className="w-3 h-3" /> Services
            </button>
            <button 
              onClick={() => onToggleStatus(vendor)} 
              className={`btn-secondary text-sm px-3 ${isInactive ? 'text-green-600' : 'text-gray-500'}`}
              title={isInactive ? 'Reactivate' : 'Deactivate'}
            >
              {isInactive ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
            </button>
            <button 
              onClick={() => onDelete(vendor)} 
              className="btn-secondary text-sm px-3 text-red-500 hover:text-red-700 hover:bg-red-50"
              title="Delete vendor"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function VendorsList() {
  const [vendors, setVendors] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [search, setSearch] = useState('');
  const [marketFilter, setMarketFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [preferredOnly, setPreferredOnly] = useState(false);
  
  // Modals
  const [showVendorModal, setShowVendorModal] = useState(null);
  const [showServicesModal, setShowServicesModal] = useState(null);
  const [showAddMarket, setShowAddMarket] = useState(false);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showManageMarkets, setShowManageMarkets] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadData = async () => {
    try {
      const [vendorsData, marketsData, categoriesData] = await Promise.all([
        api.get('/vendors'),
        api.get('/markets'),
        api.get('/event-categories')
      ]);
      setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData.vendors || []);
      setMarkets(Array.isArray(marketsData) ? marketsData : marketsData.markets || []);
      setCategories(Array.isArray(categoriesData) ? categoriesData : categoriesData.categories || []);
    } catch (err) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getRegion = (marketName) => markets.find(m => m.name === marketName)?.region || 'Other';

  // Filter vendors - NOW includes additional categories
  const filteredVendors = vendors.filter(v => {
    if (!showInactive && v.status === 'INACTIVE') return false;
    if (preferredOnly && !v.isPreferred) return false;
    
    if (marketFilter) {
      const inHome = v.homeMarket === marketFilter;
      const inAdditional = v.additionalMarkets?.includes(marketFilter);
      const inRegion = v.willTravel && getRegion(v.homeMarket) === getRegion(marketFilter);
      if (!inHome && !inAdditional && !inRegion) return false;
    }
    
    // UPDATED: Match category filter against both primary AND additional categories
    if (categoryFilter) {
      const isPrimary = v.category === categoryFilter;
      const isAdditional = v.additionalCategories?.includes(categoryFilter);
      if (!isPrimary && !isAdditional) return false;
    }
    
    if (search) {
      const q = search.toLowerCase();
      const match = v.name.toLowerCase().includes(q) ||
        v.contactName?.toLowerCase().includes(q) ||
        v.notes?.toLowerCase().includes(q) ||
        v.email?.toLowerCase().includes(q) ||
        v.address?.toLowerCase().includes(q);
      if (!match) return false;
    }
    
    return true;
  });

  // Group by relevance when market is selected
  const groupedVendors = marketFilter ? {
    homeMarket: filteredVendors.filter(v => v.homeMarket === marketFilter),
    alsoServices: filteredVendors.filter(v => v.homeMarket !== marketFilter && v.additionalMarkets?.includes(marketFilter)),
    willTravel: filteredVendors.filter(v => v.homeMarket !== marketFilter && !v.additionalMarkets?.includes(marketFilter) && v.willTravel)
  } : null;

  // NEW: Group by category relevance when category is selected
  const groupedByCategory = categoryFilter ? {
    primary: filteredVendors.filter(v => v.category === categoryFilter),
    alsoProvides: filteredVendors.filter(v => v.category !== categoryFilter && v.additionalCategories?.includes(categoryFilter))
  } : null;

  const handleToggleStatus = async (vendor) => {
    try {
      const newStatus = vendor.status === 'ACTIVE' ? 'INACTIVE' : 'ACTIVE';
      await api.put(`/vendors/${vendor.id}`, { status: newStatus });
      loadData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async () => {
    if (!showDeleteConfirm) return;
    setDeleting(true);
    try {
      await api.delete(`/vendors/${showDeleteConfirm.id}`);
      setShowDeleteConfirm(null);
      loadData();
    } catch (err) {
      alert(err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-hub-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Vendors</h1>
          <p className="text-gray-500">Your vendor network • {vendors.filter(v => v.status !== 'INACTIVE').length} active</p>
        </div>
        <div className="flex gap-2">
          <a href="/admin/vendors/discovery" className="btn-secondary">
            <Sparkles className="w-4 h-4" /> AI Discovery
          </a>
          <button onClick={() => setShowVendorModal({})} className="btn-primary">
            <Plus className="w-4 h-4" /> Add Vendor
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex flex-wrap gap-4">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search vendors, contacts..."
                className="input pl-10"
              />
            </div>
          </div>

          {/* Market Filter */}
          <div className="w-56">
            <div className="flex gap-1">
              <select value={marketFilter} onChange={e => setMarketFilter(e.target.value)} className="input flex-1">
                <option value="">All Markets</option>
                {markets.filter(m => m.active).map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
              </select>
              <button onClick={() => setShowAddMarket(true)} className="btn-secondary px-2" title="Add Market">
                <Plus className="w-4 h-4" />
              </button>
              <button onClick={() => setShowManageMarkets(true)} className="btn-secondary px-2" title="Manage Markets">
                <Settings className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Category Filter */}
          <div className="w-56">
            <div className="flex gap-1">
              <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)} className="input flex-1">
                <option value="">All Categories</option>
                {categories.filter(c => c.active).map(c => <option key={c.id} value={c.name}>{c.label}</option>)}
              </select>
              <button onClick={() => setShowAddCategory(true)} className="btn-secondary px-2" title="Add Category">
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={preferredOnly} onChange={e => setPreferredOnly(e.target.checked)} className="w-4 h-4 rounded" />
              <Star className="w-4 h-4 text-amber-500" /> Preferred
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} className="w-4 h-4 rounded" />
              Show Inactive
            </label>
          </div>
        </div>
      </div>

      {/* Results */}
      {filteredVendors.length === 0 ? (
        <div className="card p-12 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
          <h3 className="font-semibold text-gray-700 mb-1">No vendors found</h3>
          <p className="text-gray-500 text-sm mb-4">
            {vendors.length === 0 ? 'Start building your vendor network' : 'Try adjusting your filters'}
          </p>
          <div className="flex gap-3 justify-center">
            <button onClick={() => setShowVendorModal({})} className="btn-primary">
              <Plus className="w-4 h-4" /> Add Vendor
            </button>
            <a href="/admin/vendors/discovery" className="btn-secondary">
              <Sparkles className="w-4 h-4" /> Discover Vendors
            </a>
          </div>
        </div>
      ) : categoryFilter && groupedByCategory && !marketFilter ? (
        // Group by category relevance when only category filter is active
        <div className="space-y-6">
          {groupedByCategory.primary.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <Tag className="w-4 h-4" /> Primary Category ({groupedByCategory.primary.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupedByCategory.primary.map(v => (
                  <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
                ))}
              </div>
            </div>
          )}
          
          {groupedByCategory.alsoProvides.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Also Provides This Service ({groupedByCategory.alsoProvides.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupedByCategory.alsoProvides.map(v => (
                  <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : marketFilter && groupedVendors ? (
        <div className="space-y-6">
          {groupedVendors.homeMarket.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" /> Based in {marketFilter} ({groupedVendors.homeMarket.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupedVendors.homeMarket.map(v => (
                  <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
                ))}
              </div>
            </div>
          )}
          
          {groupedVendors.alsoServices.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-3">Also Services {marketFilter} ({groupedVendors.alsoServices.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupedVendors.alsoServices.map(v => (
                  <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
                ))}
              </div>
            </div>
          )}
          
          {groupedVendors.willTravel.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-500 mb-3 flex items-center gap-2">
                <Plane className="w-4 h-4" /> Will Travel ({groupedVendors.willTravel.length})
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {groupedVendors.willTravel.map(v => (
                  <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filteredVendors.map(v => (
            <VendorCard key={v.id} vendor={v} categories={categories} onEdit={setShowVendorModal} onServices={setShowServicesModal} onToggleStatus={handleToggleStatus} onDelete={setShowDeleteConfirm} />
          ))}
        </div>
      )}

      {/* Modals */}
      {showVendorModal && (
        <VendorModal 
          vendor={showVendorModal.id ? showVendorModal : null}
          markets={markets.filter(m => m.active)}
          categories={categories.filter(c => c.active)}
          onSave={() => { setShowVendorModal(null); loadData(); }} 
          onClose={() => setShowVendorModal(null)} 
        />
      )}
      
      {showServicesModal && (
        <ServicesModal 
          vendor={showServicesModal} 
          onClose={() => setShowServicesModal(null)} 
          onUpdate={loadData}
        />
      )}

      {showAddMarket && (
        <AddMarketModal onSave={() => { setShowAddMarket(false); loadData(); }} onClose={() => setShowAddMarket(false)} />
      )}

      {showAddCategory && (
        <AddCategoryModal onSave={() => { setShowAddCategory(false); loadData(); }} onClose={() => setShowAddCategory(false)} />
      )}

      {showDeleteConfirm && (
        <ConfirmDeleteModal 
          vendor={showDeleteConfirm}
          onConfirm={handleDelete}
          onClose={() => setShowDeleteConfirm(null)}
          deleting={deleting}
        />
      )}

      {showManageMarkets && (
        <ManageMarketsModal 
          markets={markets}
          vendors={vendors}
          onUpdate={loadData}
          onClose={() => setShowManageMarkets(false)}
        />
      )}
    </div>
  );
}
